import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { API } from '../../api';
import type { SharePointConfig } from '../../types/sharepoint';
import DosenButton from '../ui/Buttons/DosenButton';
import { MOBILE_SX, useIsMobileApp } from '../../mobile/mobileApp';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
  orgId: string;
}

type Step = 'instructions' | 'credentials' | 'connecting';

const EMPTY_CONFIG: SharePointConfig = {
  tenantId: '',
  clientId: '',
  clientSecret: '',
  siteUrl: '',
  libraryPath: ''
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#1A1A1A',
    borderRadius: '6px',
    color: '#FDFDFD',
    fontSize: 14,
    '& fieldset': { borderColor: '#444444' },
    '&:hover fieldset': { borderColor: '#868686' },
    '&.Mui-focused fieldset': { borderColor: '#868686' }
  },
  '& .MuiInputLabel-root': { color: '#868686', fontSize: 14 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#B6B6B6' },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: '#868686' }
};

export default function SharePointConnectModal({ isOpen, onClose, onConnected, orgId }: Props) {
  const [step, setStep] = useState<Step>('instructions');
  // A phone: the whole screen, scrolling, so the fields and buttons below the steps stay reachable.
  const isMobileApp = useIsMobileApp();
  const [config, setConfig] = useState<SharePointConfig>(EMPTY_CONFIG);
  const [showSecret, setShowSecret] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const handleClose = () => {
    setStep('instructions');
    setConfig(EMPTY_CONFIG);
    setTestStatus('idle');
    setTestError(null);
    setConnecting(false);
    onClose();
  };

  const handleFieldChange = (field: keyof SharePointConfig) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig((prev) => ({ ...prev, [field]: e.target.value }));
    // Reset test status on any field change
    if (testStatus !== 'idle') {
      setTestStatus('idle');
      setTestError(null);
    }
  };

  const isFormFilledEnough =
    config.tenantId.trim() !== '' &&
    config.clientId.trim() !== '' &&
    config.clientSecret.trim() !== '' &&
    config.siteUrl.trim() !== '';

  const handleTest = async () => {
    setTestStatus('testing');
    setTestError(null);
    try {
      const result = await API.testSharePointConnection({
        tenantId: config.tenantId.trim(),
        clientId: config.clientId.trim(),
        clientSecret: config.clientSecret.trim(),
        siteUrl: config.siteUrl.trim(),
        ...(config.libraryPath?.trim() ? { libraryPath: config.libraryPath.trim() } : {})
      });
      if (result.success) {
        setTestStatus('success');
      } else {
        setTestStatus('error');
        setTestError(result.error ?? 'Connection test failed. Please check your credentials.');
      }
    } catch {
      setTestStatus('error');
      setTestError('Connection test failed. Please check your credentials and try again.');
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await API.saveSharePointConnection(orgId, {
        tenantId: config.tenantId.trim(),
        clientId: config.clientId.trim(),
        clientSecret: config.clientSecret.trim(),
        siteUrl: config.siteUrl.trim(),
        ...(config.libraryPath?.trim() ? { libraryPath: config.libraryPath.trim() } : {})
      });
      onConnected();
      handleClose();
    } catch {
      setTestStatus('error');
      setTestError('Failed to save connection. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobileApp}
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          borderRadius: 2,
          border: '1px solid #444444',
          overflow: 'hidden',
          maxWidth: 550,
          [MOBILE_SX]: {
            m: 0,
            width: '100%',
            maxWidth: '100%',
            border: 'none',
            borderRadius: 0,
            overflowY: 'auto',
            overflowWrap: 'anywhere'
          }
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
          <SharePointIcon size={22} />
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700 }}>
            Connect SharePoint
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#FDFDFD' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {step === 'instructions' && (
          <>
            <Typography sx={{ color: '#B6B6B6', fontSize: 14, lineHeight: '22px' }}>
              To connect SharePoint, your IT department needs to register an Azure AD app and grant it access
              to your SharePoint site. This only needs to be done once.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: '6px',
                background: '#1A1A1A',
                border: '1px solid #2A2A2A',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
                What your IT team needs to do:
              </Typography>
              {[
                'Register a new app in Azure Active Directory',
                'Grant Sites.Read.All permission (Application permission)',
                'Create a client secret and note the Tenant ID, Client ID, and Client Secret',
                'Share those credentials with you'
              ].map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: '1px'
                    }}
                  >
                    <Typography sx={{ color: '#B6B6B6', fontSize: 11, fontWeight: 700 }}>{i + 1}</Typography>
                  </Box>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 13, lineHeight: '20px' }}>{step}</Typography>
                </Box>
              ))}
            </Box>

            <Box
              component="a"
              href="https://docs.dosen.io/integrations/sharepoint"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#B6B6B6',
                fontSize: 13,
                textDecoration: 'none',
                '&:hover': { color: '#FDFDFD' }
              }}
            >
              View the full setup guide
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <DosenButton onClick={() => setStep('credentials')} sizeVariant="sm">
                Continue to Connect
              </DosenButton>
            </Box>
          </>
        )}

        {step === 'credentials' && !connecting && (
          <>
            <TextField
              label="Tenant ID"
              fullWidth
              value={config.tenantId}
              onChange={handleFieldChange('tenantId')}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Client ID"
              fullWidth
              value={config.clientId}
              onChange={handleFieldChange('clientId')}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Client Secret"
              fullWidth
              value={config.clientSecret}
              onChange={handleFieldChange('clientSecret')}
              type={showSecret ? 'text' : 'password'}
              size="small"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSecret((v) => !v)}
                      edge="end"
                      sx={{ color: '#868686', p: 0.5 }}
                    >
                      {showSecret ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="SharePoint Site URL"
              fullWidth
              value={config.siteUrl}
              onChange={handleFieldChange('siteUrl')}
              size="small"
              placeholder="https://yourcompany.sharepoint.com/sites/example"
              sx={inputSx}
            />
            <TextField
              label="Library Path (optional)"
              fullWidth
              value={config.libraryPath ?? ''}
              onChange={handleFieldChange('libraryPath')}
              size="small"
              placeholder="/Shared Documents/Policies"
              sx={inputSx}
            />

            {/* Test status feedback */}
            {testStatus === 'success' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                <Typography sx={{ color: '#4CAF50', fontSize: 13 }}>
                  Connection successful
                </Typography>
              </Box>
            )}
            {testStatus === 'error' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorOutlineIcon sx={{ color: '#F44336', fontSize: 18 }} />
                <Typography sx={{ color: '#F44336', fontSize: 13 }}>
                  {testError}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
              <DosenButton
                onClick={handleTest}
                disabled={!isFormFilledEnough || testStatus === 'testing'}
                tone="basic"
                sizeVariant="sm"
              >
                {testStatus === 'testing' ? 'Testing...' : 'Test Connection'}
              </DosenButton>

              <DosenButton
                onClick={handleConnect}
                disabled={testStatus !== 'success'}
                sizeVariant="sm"
              >
                Connect
              </DosenButton>
            </Box>
          </>
        )}

        {connecting && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={32} sx={{ color: '#FDFDFD' }} />
            <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>Connecting to SharePoint...</Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

function SharePointIcon({ size = 20 }: { size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="3" fill="#0078D4" />
        <rect x="3" y="3" width="8" height="8" rx="1.5" fill="#fff" fillOpacity="0.9" />
        <rect x="9" y="9" width="8" height="8" rx="1.5" fill="#fff" fillOpacity="0.6" />
        <rect x="3" y="11" width="6" height="6" rx="1.5" fill="#fff" fillOpacity="0.45" />
      </svg>
    </Box>
  );
}
